import { Account } from '~/types/account';
import { Card } from '~/types/cards';
import { Category, CategoryFilterValue, Subcategory } from '~/types/category';
import { Company } from '~/types/company';
import { CostCenterFilterValue } from '~/types/cost-center';
import { MetaDataCursorPage } from '~/types/jsonapi';
import { Money } from '~/types/money';
import { Reconciliation } from '~/types/reconciliation';
import { TransactionRuleDirection } from '~/types/rule';
import { SortInfo } from '~/utils/jsonapi-query';
import { BookkeepingTransaction } from './general-bookkeeping';
import { Payment } from './payment';
import { TransactionAllocation, TransactionAllocationUpdatePayload } from './transaction-allocation';
import { TransactionAttachment } from './transaction-attachment';

export const TransactionResourceType = 'transaction';

export interface TransactionsPreviewFilter {
  keywords: string[];
  accountId: string | null;
  flowDirection: TransactionFlowDirection | null;
  categoryStatus: 'all' | 'uncategorized';
  transactionTypes: TransactionType[];
}

export interface TransactionMetadata {
  total: number;
  totalUncategorised: number;
  totals: { currency: string; negative: Money; positive: Money }[];
  totalsEur: { currency: string; negative: Money; positive: Money };
  totalsEurSucceeded: { currency: string; negative: Money; positive: Money };
}

export type TransactionSorting = 'date' | 'amount';

interface TransactionQueryFilters {
  amount: [number | null, number | null];
  date: [string, string];
}

export interface TransactionProposedRulesItem {
  count: number;
  keyword: string;
  direction: TransactionRuleDirection;
}

export interface TransactionProposedRules {
  data: TransactionProposedRulesItem[];
}

export enum TransactionFlowDirection {
  Inflow = 'inflow',
  Outflow = 'outflow',
}

export enum TransactionFlowDirectionUppercase {
  INFLOW = 'INFLOW',
  OUTFLOW = 'OUTFLOW',
}

export const TransactionsKeys = {
  all: ['Transactions'],
  company: (companyId?: string, filters?: TransactionQueryFilters) => ['Transactions', companyId, filters],
  preview: (companyId: string | undefined, filter: TransactionsPreviewFilter) => [
    'Transactions',
    companyId,
    'Preview',
    { ...filter, keywords: filter.keywords.join('|') },
  ],
  single: (companyId: string | undefined, id: Transaction['id']) => ['Transactions', companyId, 'Single', id],
  metadataPreview: (companyId: string, filters: TransactionsPreviewFilter) => [
    'Transactions',
    companyId,
    'MetadataPreview',
    filters,
  ],
  metadata: (companyId: string, filters: TransactionMetadataFilter) => ['Transactions', companyId, 'Metadata', filters],
  metadataFields: (companyId: string, filters: TransactionsFiltersFields) => [
    'Transactions',
    companyId,
    'MetadataFields',
    filters,
  ],
  proposedRules: (companyId: string, direction?: TransactionRuleDirection) => [
    'Transactions',
    companyId,
    'ProposedRules',
    direction,
  ],
  reconciliations: (companyId: string, id: Transaction['id'] | null) => [
    'Transactions',
    companyId,
    'Reconciliation',
    id,
  ],
  syncedDeposits: (companyId: string | undefined, id: Transaction['id'] | null) => [
    'Transactions',
    companyId,
    'SyncedDeposits',
    id,
  ],
  firstAccountTransaction: (companyId: string, accountId: string) => [
    'Transactions',
    companyId,
    'FirstByAccount',
    accountId,
  ],
  lastAccountTransaction: (companyId: string, accountId: string) => [
    'Transactions',
    companyId,
    'LastByAccount',
    accountId,
  ],
  infiniteTable: (companyId: string, filters: TransactionsFiltersFields, sorting: SortInfo<TransactionSorting>) => [
    'Transactions',
    'Infinite',
    companyId,
    filters,
    sorting,
  ],
  infiniteTableCompany: (companyId: string) => ['Transactions', 'Infinite', companyId],
};

export enum VerificationStatus {
  VERIFIED = 'VERIFIED',
  TO_VERIFY = 'TO_VERIFY',
}

export enum TransactionType {
  CARDS = 'CARDS',
  CARD_AUTHORIZATION = 'CARD_AUTHORIZATION',
  CASH = 'CASH',
  CHECK = 'CHECK',
  DEPOSITS = 'DEPOSITS',
  DIRECT_DEBIT = 'DIRECT_DEBIT',
  FEES = 'FEES',
  FINANCIAL_EXPENSES = 'FINANCIAL_EXPENSES',
  FINANCING = 'FINANCING',
  INTERNAL_TRANSFER = 'INTERNAL_TRANSFER',
  OTHER = 'OTHER',
  POS = 'POS',
  RIBA = 'RIBA',
  TAXES = 'TAXES',
  TRANSFER = 'TRANSFER',
  WITHDRAWALS = 'WITHDRAWALS',
}

export enum TransactionStatus {
  Pending = 'PENDING',
  Booked = 'BOOKED',
  Rejected = 'REJECTED',
  Canceled = 'CANCELED',
}

export enum TransactionHiddenStatus {
  Upcoming = 'UPCOMING',
}

export enum DeduplicationDecision {
  DUPLICATE = 'duplicate',
  POSSIBLE_DUPLICATE = 'possible_duplicate',
}

export enum DeduplicationOutcome {
  DUPLICATE = 'duplicate',
  NOT_DUPLICATE = 'not_duplicate',
}

export interface DeduplicationOutcomeParameters {
  transactionId: string;
  originalTransactionId: string;
  outcome: DeduplicationOutcome;
}

export interface TransactionDeduplication {
  id: string;
  decision: DeduplicationDecision;
  score: number;
  baseScore: number;
  similarity: number;
  postSimilarity: number;
  comparisonDatetime: string;
  createdAt: string;
  description: string;
  targetDescription: string;
  replacedDescription: string;
  replacedTargetDescription: string;
  codes: string[];
  targetCodes: string[];
  targetTransaction?: Transaction;
}

export enum CategorizationSources {
  Automatic = 'AUTOMATIC',
  User = 'USER',
  RuleEngine = 'RULE_ENGINE',
  Unknown = 'UNKNOWN',
}

export const CATEGORIZATION_SOURCE_OTHER = 'OTHER' as const;

export type CategorizationSourceFilter = CategorizationSources | typeof CATEGORIZATION_SOURCE_OTHER;

export interface Transaction {
  id: string;
  amount: Money;
  calculatedBankFees: Money | null;
  attachments?: TransactionAttachment[];
  counterpartName: string | null;
  date: string;
  description: string;
  cleanDescription?: string;
  verificationStatus: VerificationStatus;
  notes?: string;
  account: Account;
  reconciliations?: Reconciliation[];
  category?: Category;
  subcategory?: Subcategory;
  hiddenAt: Date | null;
  transactionType?: TransactionType;
  instant?: boolean;
  status: TransactionStatus;
  rejectionReason?: string | null;
  allocations?: TransactionAllocation[];
  payment?: Payment;
  categorizationSource?: CategorizationSources;
  card?: Card;
  bookkeepingTransaction?: BookkeepingTransaction;
  depositTransaction?: Transaction | null;
  deduplication?: TransactionDeduplication | null;
}

export interface Total {
  positive: Money;
  negative: Money;
}

export interface TransactionMeta {
  page: MetaDataCursorPage;
}

export interface TransactionCreatePayload {
  company: {
    id: Company['id'];
  };
  account: {
    id: Account['id'];
  };
  category?: {
    id: Category['id'];
  };
  subcategory?: {
    id: Subcategory['id'];
  };
  amount: Money;
  transactionType: TransactionType | null;
  counterpartName?: string;
  date: string;
  description: string;
}

export interface TransactionUpdatePayload {
  id: Transaction['id'];
  notes?: Transaction['notes'] | null;
  verificationStatus?: Transaction['verificationStatus'];
  hiddenAt?: Transaction['hiddenAt'];
  account?: {
    id: Account['id'];
  };
  category?: {
    id: Category['id'];
  } | null;
  subcategory?: {
    id: Subcategory['id'];
  } | null;
  amount?: Money;
  transactionType?: TransactionType | null;
  counterpartName?: string;
  date?: string;
  description?: string;
  allocations?: TransactionAllocationUpdatePayload[] | null;
}

export interface TransactionsFiltersFields {
  search: string;
  date: [string | null, string | null];
  currency: string | null;
  accountId: string[] | null;
  status: TransactionStatus | null;
  verificationStatus: VerificationStatus | null;
  category: CategoryFilterValue | null;
  costCenter?: CostCenterFilterValue;
  categorizationSource?: CategorizationSourceFilter;
  amountRange: [number | null, number | null, TransactionFlowDirection | null];
  hidden: boolean | null;
  transactionType: TransactionType[];
  hasBookkeepingTransactions?: boolean | null;
  selected?: Transaction['id'][];
  excluded?: Transaction['id'][];
  excludeHiddenTransactions?: boolean;
  excludeFailedTransactions?: boolean;
  cardHolder: string[];
}

export type TransactionMetadataFilter = TransactionsFiltersFields;

export type CashbookExportFormat = 'standard' | 'advanced';

export interface BulkTransactionUpdateAttributes {
  hiddenAt?: Date | null;
  categoryId?: Category['id'] | null;
  subcategoryId?: Subcategory['id'] | null;
  verificationStatus?: VerificationStatus;
}

export interface ShareCashbookParameters {
  companyId: string;
  accountIds: Account['id'][];
  from: Date;
  to: Date;
  email: string;
}
